export interface SplitPaneBounds {
  readonly min: number;
  readonly max: number;
  readonly defaultRatio: number;
}

function assertSplitPaneBounds(
  bounds: Readonly<Pick<SplitPaneBounds, "min" | "max">>
): void {
  if (
    !Number.isFinite(bounds.min) ||
    !Number.isFinite(bounds.max) ||
    bounds.min < 0 ||
    bounds.max > 100 ||
    bounds.min >= bounds.max
  ) {
    throw new RangeError("Split pane bounds must satisfy 0 <= min < max <= 100.");
  }
}

export function splitPaneBounds(
  min: number,
  max: number,
  defaultRatio: number
): SplitPaneBounds {
  assertSplitPaneBounds({ min, max });
  if (!Number.isFinite(defaultRatio)) {
    throw new RangeError("Split pane default ratio must be finite.");
  }
  return Object.freeze({
    min,
    max,
    defaultRatio: Math.min(max, Math.max(min, defaultRatio))
  });
}

export function clampSplitPaneRatio(
  ratio: number,
  bounds: Pick<SplitPaneBounds, "min" | "max">
): number {
  assertSplitPaneBounds(bounds);
  if (!Number.isFinite(ratio)) {
    throw new RangeError("Split pane ratio must be finite.");
  }
  return Math.min(bounds.max, Math.max(bounds.min, ratio));
}

export function splitPanePixelRatio(input: Readonly<{
  firstPaneSize: number;
  containerSize: number;
  bounds: Pick<SplitPaneBounds, "min" | "max">;
}>): number | null {
  assertSplitPaneBounds(input.bounds);
  if (
    !Number.isFinite(input.firstPaneSize) ||
    input.firstPaneSize <= 0 ||
    !Number.isFinite(input.containerSize) ||
    input.containerSize <= 0
  ) {
    return null;
  }
  return clampSplitPaneRatio(
    (input.firstPaneSize / input.containerSize) * 100,
    input.bounds
  );
}

export function splitPanePointerRatio(input: Readonly<{
  clientPosition: number;
  containerStart: number;
  containerSize: number;
  bounds: Pick<SplitPaneBounds, "min" | "max">;
}>): number | null {
  assertSplitPaneBounds(input.bounds);
  if (
    !Number.isFinite(input.clientPosition) ||
    !Number.isFinite(input.containerStart) ||
    !Number.isFinite(input.containerSize) ||
    input.containerSize <= 0
  ) {
    return null;
  }
  const ratio =
    ((input.clientPosition - input.containerStart) / input.containerSize) * 100;
  return Number.isFinite(ratio)
    ? clampSplitPaneRatio(ratio, input.bounds)
    : null;
}

export function splitPaneKeyboardRatio(input: Readonly<{
  ratio: number;
  key: string;
  direction: "vertical" | "horizontal";
  shiftKey?: boolean;
  bounds: Pick<SplitPaneBounds, "min" | "max">;
}>): number | null {
  const step = input.shiftKey ? 10 : 2;
  if (input.key === "Home") return input.bounds.min;
  if (input.key === "End") return input.bounds.max;
  const decrement =
    input.direction === "vertical" ? "ArrowLeft" : "ArrowUp";
  const increment =
    input.direction === "vertical" ? "ArrowRight" : "ArrowDown";
  if (input.key === decrement) {
    return clampSplitPaneRatio(input.ratio - step, input.bounds);
  }
  if (input.key === increment) {
    return clampSplitPaneRatio(input.ratio + step, input.bounds);
  }
  return null;
}
